import { Play } from 'lucide-react';
import { useRef, useState } from 'react';
import type { GenreExample } from '../../../data/portfolio-examples';
import { hasMedia } from '../../../data/portfolio-examples';
import EmptyMediaSlot from './EmptyMediaSlot';

type Props = {
  example: GenreExample;
  /** Repetido em cada card: quem entra pelo meio da prateleira também vê o preço. */
  price: string;
  registerMedia: (id: string, element: HTMLMediaElement | null) => void;
  claimPlayback: (id: string) => void;
};

export default function GenreVideoCard({ example, price, registerMedia, claimPlayback }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [started, setStarted] = useState(false);
  const ready = hasMedia(example.video);

  // O play precisa sair do próprio gesto: iOS bloqueia áudio se a chamada
  // acontecer depois de um re-render.
  const start = () => {
    const video = videoRef.current;
    if (!video) return;
    setStarted(true);
    video.preload = 'auto';
    void video.play().catch(() => {});
  };

  const detail = [
    example.video.duration ? `Música inteira, ${example.video.duration}` : 'Música inteira',
    example.photoCount ? `${example.photoCount} fotos` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="ex-card">
      <div className="ex-card__frame">
        {ready ? (
          <>
            <video
              ref={(element) => {
                videoRef.current = element;
                registerMedia(example.id, element);
              }}
              className="ex-card__video"
              src={example.video.src}
              poster={example.video.poster || undefined}
              preload="none"
              playsInline
              controls={started}
              onPlay={() => claimPlayback(example.id)}
            />
            {!started && (
              <button
                type="button"
                className="ex-card__open"
                onClick={start}
                aria-label={`Assistir o exemplo de ${example.genre}, ${example.occasion}`}
              >
                <span className="ex-card__scrim" />
                <span className="ex-card__play">
                  <Play size={26} fill="currentColor" strokeWidth={0} aria-hidden="true" />
                </span>
              </button>
            )}
          </>
        ) : (
          <EmptyMediaSlot spec="Vídeo 9:16 · música completa · fotos da pessoa" />
        )}
      </div>

      <div className="ex-card__meta">
        <span className="ex-card__genre">{example.genre}</span>
        <p className="ex-card__occasion">{example.occasion}</p>
        <p className="ex-card__plan">Vídeo com fotos · {price}</p>
        {ready && <p className="ex-card__duration">{detail}</p>}
      </div>
    </article>
  );
}
